import { useState, useEffect, useMemo } from 'react';
import type { ComponentData } from '../types';
import { Header } from './Header';
import { ComponentCard } from './ComponentCard';

export function JsonCopier() {
    const [components, setComponents] = useState<ComponentData[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedCategory, setSelectedCategory] = useState<string>("All");
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [textAlignment, setTextAlignment] = useState<string>("");
    const [contentAlignment, setContentAlignment] = useState<string>("");

    useEffect(() => {
        fetch('/api/components')
            .then(res => res.json())
            .then(data => {
                setComponents(data);
                setIsLoading(false);
            })
            .catch(err => {
                console.error("Failed to fetch components:", err);
                setIsLoading(false);
            });
    }, []);

    const categories = useMemo(() => {
        const uniqueCategories = Array.from(new Set(components.map(c => c.category || "uncategorized")));
        return ["All", ...uniqueCategories.sort()];
    }, [components]);

    const filteredComponents = useMemo(() => {
        let result = components;

        // Filter by category
        if (selectedCategory !== "All") {
            result = result.filter(c => (c.category || "uncategorized") === selectedCategory);
        }

        // Filter by search query
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase().trim();
            result = result.filter(c => c.name.toLowerCase().includes(query));
        }

        // Filter by text alignment
        if (textAlignment) {
            result = result.filter(c => c.text_alignment === textAlignment);
        }

        // Filter by content alignment
        if (contentAlignment) {
            result = result.filter(c => c.content_alignment === contentAlignment);
        }

        return result.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }));
    }, [components, selectedCategory, searchQuery, textAlignment, contentAlignment]);

    return (
        <div className="json-copier-container">
            <Header />

            <div className="main-layout">
                <aside className="sidebar">
                    <div className="sidebar-section">
                        <label htmlFor="search" className="sidebar-label">Search</label>
                        <input
                            id="search"
                            type="text"
                            placeholder="Search components..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="search-input"
                        />
                    </div>

                    <div className="sidebar-section">
                        <label className="sidebar-label">Categories</label>
                        <div className="category-list">
                            {categories.map(category => (
                                <button
                                    key={category}
                                    onClick={() => setSelectedCategory(category)}
                                    className={`category-button ${selectedCategory === category ? 'active' : ''}`}
                                >
                                    {category}
                                </button>
                            ))}
                        </div>
                    </div>
                </aside>

                <main className="content-area">
                    <div className="filters-bar">
                        <select
                            value={textAlignment}
                            onChange={(e) => setTextAlignment(e.target.value)}
                            className="filter-select"
                        >
                            <option value="">Text Alignment</option>
                            <option value="left">Text Align Left</option>
                            <option value="center">Text Align Center</option>
                        </select>

                        <select
                            value={contentAlignment}
                            onChange={(e) => setContentAlignment(e.target.value)}
                            className="filter-select"
                        >
                            <option value="">Content Alignment</option>
                            <option value="left">Image/Video Left</option>
                            <option value="center">Image/Video Center</option>
                            <option value="right">Image/Video Right</option>
                        </select>

                        {(textAlignment || contentAlignment) && (
                            <button onClick={() => { setTextAlignment(""); setContentAlignment(""); }} className="clear-filters-btn">
                                Clear
                            </button>
                        )}
                    </div>

                    {isLoading ? (
                        <div className="loading">Loading components...</div>
                    ) : (
                        <>
                            {filteredComponents.length > 0 ? (
                                <div className="components-grid">
                                    {filteredComponents.map(component => (
                                        <ComponentCard key={component.id} component={component} />
                                    ))}
                                </div>
                            ) : (
                                <div className="empty-state">
                                    No components found.
                                </div>
                            )}
                        </>
                    )}
                </main>
            </div>
        </div>
    );
}

export default JsonCopier;
